import React from 'react';
import { observer } from 'mobx-react';
import { Pagination } from 'ats/components/pagination/Pagination';
import { ListStore } from 'shared/stores/ListStore';
import { formatTimeAgo } from 'shared/utils';
import { JobApplicationHistoryStore } from 'ats/pages/pipeline/components/JobApplicationHistoryStore';
import { jobApplicationStore } from 'ats/pages/pipeline/components/JobApplicationStore';
import { jobPositionStore } from 'ats/pages/job-positions/JobPositionStore';
import { eventManager } from 'ats/utils/EventManager';
import Dropdown from 'ats/components/dropdown/Dropdown';
import { dialogStore } from 'components/layout/modals/DialogStore';
import { notificationStore } from 'components/layout/notifications/NotificationStore';
import JobApplicationStageLabel from 'ats/pages/pipeline/components/JobApplicationStageLabel';
import { getJobApplicationStageTheme } from 'ats/constants/jobApplicationStageTheme';
import JobApplicationHistoryCard from './JobApplicationHistoryCard';
import JobApplicationTaskCard from './JobApplicationTaskCard';
import { openJobPositionProfile } from 'ats/utils/openEntityProfile';
import HoverMenu from 'ats/components/hover-menu/HoverMenu';
import { modalStore } from 'components/layout/modals/ModalStore';
import { JobApplicationTaskCreate } from 'ats/pages/pipeline/components/JobApplicationTaskCreate';
import PendingTaskBadge from 'ats/components/pending-task-badge/PendingTaskBadge';

@observer
export class JobApplicationCard extends React.Component {
	constructor(props) {
		super(props);
		this.state = {
			isExpanded: !!props.initialExpanded
		};
		this.cardRef = React.createRef();
		this.jobApplicationHistoryStore = new JobApplicationHistoryStore();
		this.listStore = new ListStore(this.jobApplicationHistoryStore, {
			entityName: 'Job Application History'
		});
	}

	preConfigureQuery() {
		this.listStore.store.resetQuery();
		this.listStore.store.setQuery(
			'jobApplicationId',
			this.props.jobApplication.id
		);
		this.listStore.store.setQuery('sort', {
			column: 'createdAt',
			asc: false
		});
	}

	handleJobApplicationStageChanged = ({ jobApplicationId }) => {
		if (jobApplicationId === this.props.jobApplication.id) {
			this.listStore.search();
		}
	};

	async componentDidMount() {
		this.preConfigureQuery();

		eventManager.on(
			'jobApplicationStageChanged',
			this.handleJobApplicationStageChanged
		);

		if (this.props.initialExpanded && this.cardRef.current) {
			await this.listStore.search();

			setTimeout(() => {
				requestAnimationFrame(() => {
					const container = this.cardRef.current?.closest('.drawer-content');
					container?.scrollTo({
						top: this.cardRef.current.offsetTop - container.offsetTop - 65,
						behavior: 'smooth'
					});
				});
			}, 100);
		}
	}

	componentWillUnmount() {
		eventManager.off(
			'jobApplicationStageChanged',
			this.handleJobApplicationStageChanged
		);
	}

	componentDidUpdate(prevProps, prevState) {
		if (
			prevState.isExpanded !== this.state.isExpanded &&
			this.state.isExpanded
		) {
			this.listStore.search();
		}

		if (prevProps.jobApplication !== this.props.jobApplication) {
			this.preConfigureQuery();
			this.listStore.search();
		}
	}

	handlePaginationChange = value => {
		this.listStore.handlePaginationChange(
			value,
			this.listStore.store.query.pagination.limit
		);
	};

	getStage = value =>
		jobApplicationStore.stageOptions.find(stage => stage.value === value);

	getStatus = value =>
		jobApplicationStore.statusOptions.find(status => status.value === value);

	getJobStatus = value => {
		return jobPositionStore.statusOptions.find(
			status => status.value === value
		);
	};

	handleJobApplicationStageEdit = (jobApplication, stage) => {
		dialogStore.openDialog(
			{
				title: 'Update Job Application Stage',
				message: 'The Job Application Stage will be changed. Are you sure?',
				confirmLabel: 'Confirm',
				confirmButtonClass: 'is-primary'
			},
			() =>
				jobApplicationStore
					.update(jobApplication.id, { jobApplication: { stage } })
					.then(result => {
						if (result.status === 'success') {
							notificationStore.pushSuccessNotification(
								'Job Application Stage updated',
								'The Job Application Stage has been successfully updated.'
							);
							this.props.onSuccess();

							eventManager.emit('jobApplicationStageChanged', {
								jobApplicationId: jobApplication.id,
								stage: stage.value,
								applicantId: jobApplication.applicantId,
								jobId: jobApplication.job.id
							});
						} else {
							notificationStore.pushErrorNotification(
								'Update Failed',
								'Failed to update the Job Application Stage'
							);
						}
					})
		);
	};

	handleJobApplicationJobTitleClick = () => {
		openJobPositionProfile(this.props.jobApplication.job.id, {
			onSuccess: () => {
				this.props.onSuccess();
				this.listStore.search();
			}
		});
	};

	handleCreateTask = () => {
		modalStore.openModal(
			{ title: 'Add Task', size: 'xl', position: 'center' },
			<JobApplicationTaskCreate
				handleSuccess={this.props.onSuccess}
				applicant={this.props.applicant}
				job={this.props.jobApplication.job}
			/>
		);
	};

	handleJobApplicationDelete = jobApplication => {
		dialogStore.openDialog(
			{
				title: 'Delete Job Application',
				message: 'This Job Application will be deleted. Are you sure?',
				confirmLabel: 'Delete',
				confirmButtonClass: 'is-danger'
			},
			() =>
				jobApplicationStore.delete(jobApplication.id).then(result => {
					if (result.status === 'success') {
						notificationStore.pushSuccessNotification(
							'Job Application Deleted',
							'The Job Application has been successfully deleted.'
						);
						this.props.onSuccess?.();

						eventManager.emit('jobApplicationDeleted', {
							jobApplicationId: jobApplication.id,
							applicantId: jobApplication.applicantId
						});
					} else {
						notificationStore.pushErrorNotification(
							'Delete Failed',
							'Failed to delete the Job Application'
						);
					}
				})
		);
	};

	getMenuItems = () => [
		{
			label: 'Add Task',
			onClick: () => this.handleCreateTask()
		},
		{
			label: 'Unassign',
			onClick: () => this.handleJobApplicationDelete(this.props.jobApplication)
		}
	];

	render() {
		const stageData = this.getStage(this.props.jobApplication.stage);
		const statusData = this.getStatus(
			jobApplicationStore.stageToStatus[this.props.jobApplication.stage]
		);
		const jobStatusData = this.getJobStatus(
			this.props.jobApplication.job.status
		);
		const stageTheme = getJobApplicationStageTheme(stageData?.value);

		return (
			<div className="job-application-card" ref={this.cardRef}>
				<div className="job-application-card-header">
					<PendingTaskBadge
						count={this.props.jobApplication.pendingTasksCount}
						variant="count"
					/>
					<div className="job-application-card-header-left">
						<div className="job-application-card-job-title">
							<h3 onClick={this.handleJobApplicationJobTitleClick}>
								<span className="job-position-id">
									#{this.props.jobApplication.job.id}
								</span>
								{this.props.jobApplication.job.title}
							</h3>
							<span
								className="job-status"
								style={{
									color: jobStatusData?.colors.text
								}}>
								{jobStatusData?.text}
							</span>
						</div>
						<div className="job-application-card-client">
							<h3>{this.props.jobApplication.job?.client?.name}</h3>
						</div>
					</div>
					<div className="job-application-card-details">
						<div className="stage-tag">
							<Dropdown
								value={{
									...stageData,
									label: (
										<JobApplicationStageLabel
											stageData={stageData}
											statusData={statusData}
										/>
									)
								}}
								buttonTextColor={stageTheme.color}
								name="stage"
								menuClassName="stage-dropdown-menu"
								onChange={value => {
									this.handleJobApplicationStageEdit(
										this.props.jobApplication,
										value.value
									);
								}}
								hideSelectedOption
								options={jobApplicationStore.stageOptions.map(stage => {
									const statusData = this.getStatus(
										jobApplicationStore.stageToStatus[stage.value]
									);

									return {
										...stage,
										label: (
											<JobApplicationStageLabel
												stageData={stage}
												statusData={statusData}
											/>
										)
									};
								})}
							/>
						</div>
						<div className="date">
							{formatTimeAgo(new Date(this.props.jobApplication.updatedAt), {
								moreThanOneDay: true
							})}
						</div>
						<div className="actions">
							<HoverMenu
								position="left"
								buttonIcon={
									<img src="/ats/icons/three-dots-icon.svg" alt="Menu button" />
								}
								items={this.getMenuItems()}
							/>
						</div>
						<div className="toggle-button">
							<button
								onClick={() =>
									this.setState({ isExpanded: !this.state.isExpanded })
								}>
								<img
									src={`/ats/icons/${
										this.state.isExpanded
											? 'chevron-up-icon.svg'
											: 'chevron-down-icon.svg'
									}`}
									alt="Toggle"
								/>
							</button>
						</div>
					</div>
				</div>
				{this.state.isExpanded && (
					<div className="job-application-history">
						{this.listStore.data.length > 0 && (
							<>
								<div className="job-application-history-list">
									{this.listStore.data.map(item =>
										item.source === 'task' ? (
											<JobApplicationTaskCard
												task={item}
												onSuccess={() => {
													this.listStore.search();
												}}
											/>
										) : (
											<JobApplicationHistoryCard
												history={item}
												onSuccess={() => {
													this.listStore.search();
												}}
											/>
										)
									)}
								</div>
								<Pagination
									loading={this.listStore.loading}
									pagination={this.listStore.store.query.pagination}
									onPaginationChange={this.handlePaginationChange}
								/>
							</>
						)}
					</div>
				)}
			</div>
		);
	}
}
